/**
 * ADR-0240 Slice 2b — "Reports" page under the Agency nav group.
 *
 * Flow:
 *   1. On mount, GET `/v1/agency/reports/runs` → remaining budget, cap,
 *      agency display name, and the most recent 20 runs. The budget
 *      line shows before any submit; past runs survive a tab close.
 *      When the URL carries `?run=<run_id>`, that run is auto-selected
 *      and polled.
 *   2. Agency pastes domains (one per line, up to 50) into a textarea
 *      OR uploads a .txt / .csv file. The file reader stays local
 *      (no upload surface on the API — the request body is JSON).
 *   3. "Submit" POSTs to `/v1/agency/reports/bulk-import`. The server
 *      enqueues one row per valid domain, marks over-cap rows
 *      `rate_limited`, and returns the initial state.
 *   4. The page polls `/v1/agency/reports/bulk-import/:run_id` every
 *      5 s while any row is `queued` / `running`. The CSV export +
 *      per-row "Send to…" affordances activate as rows reach `done`.
 *      Export CSV stays disabled until at least one row is done.
 *   5. "Send to…" opens a dialog with recipient email + a mandatory
 *      consent checkbox + a preview of the subject line + consent copy
 *      templated with the agency's display name. Submission posts to
 *      `/v1/agency/reports/:row_id/send`.
 *
 * URL state: the selected `run_id` is persisted as `?run=<run_id>` via
 * replaceState so refresh preserves the open run. On submit, the new
 * run_id replaces the URL param.
 *
 * Sill voice (CLAUDE.md §8): precise, technical, no exclamation marks,
 * no "amazing". Error copy maps every ApiError kind to a plain
 * sentence via `agency-reports.ts`'s `explainApiError`.
 *
 * Home-session only; the component renders a friendly "return home"
 * message on an acting session and "agency is not enabled" on a non-
 * agency account (gates are server-enforced too — the handler returns
 * 403 — this is just UX).
 */

import { AlertCircle, Check, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { defaultAgencySendSubject } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import {
  checkExistingDomains,
  getReportsOverview,
  pollBulkRun,
  sendRowReport,
  submitBulkImport,
  type AgencyReportsOverviewWire,
  type BulkRunWire,
  type BulkRowWire,
  type CheckExistingResultRow,
  type ReportTableRow,
  type SendRowReportResult,
  type SubmitBulkImportResult,
} from '../../api/agency-reports.js';
import { Button, Card, Modal, NumLabel } from '../../components/atoms/index.js';
import { useIsEmbedded } from '../agency-portal/portal-shell-context.js';
import { useC } from '../../lib/theme.js';
import {
  detectColumns,
  parseDelimited,
  parseProspectGrid,
  type ParseResult,
  type ParsedProspectRow,
  type SkippedProspectRow,
} from '../../lib/prospect-import.js';
import { readXlsx } from '../../lib/xlsx-lite.js';
import { ImportPreview, type ImportColumnUse } from './ImportPreview.js';
import { ReportsTable } from './ReportsTable.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const POLL_INTERVAL_MS = 5000;

/** Default copy stand-in for the agency display name — used only when
 *  the API didn't resolve one (brand-new account without a configured
 *  white-label row AND an empty account.name, which should not happen
 *  in practice but keeps the dialog copy sensible). */
const DEFAULT_AGENCY_NAME = 'us';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | {
      kind: 'ok';
      run_id: string;
      /** ADR-0240 Portal v2 Slice 2 — per-row skipped reasons from
       *  the just-completed submit. Rendered as a one-line amber
       *  summary above the run detail. */
      skipped?: ReadonlyArray<{ input: string; reason: string }>;
    }
  | { kind: 'error'; message: string };

interface SendDialogState {
  readonly row: BulkRowWire;
  readonly email: string;
  readonly consent: boolean;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly sent_url: string | null;
  /** ADR-0240 Portal v2 Slice 7 — editable subject + first-name
   *  prefill. Both pre-populated from the import row's contact
   *  fields when present. */
  readonly subject: string;
  readonly first_name: string;
}

function humanizeSubmit(r: Exclude<SubmitBulkImportResult, { kind: 'ok' }>): string {
  switch (r.kind) {
    case 'empty':
      return 'Add at least one domain.';
    case 'too_many_domains':
      return `Only ${r.max} domains per batch. Split your list and try again.`;
    case 'duplicate_domain':
      // ADR-0240 Portal v2 Slice 2 (2026-10-10) — unique_violation
      // from the server surfaces here with a specific copy, never
      // the raw `conflict:409`.
      return 'One of these domains is already in another import. Try again in a minute.';
    case 'rate_limited':
      return r.message;
    case 'forbidden':
      return 'Agency access is not enabled on this account.';
    case 'error':
      return r.message;
  }
}

/** ADR-0240 Portal v2 Slice 2 (2026-10-10) — per-row skip reason
 *  copy for the import outcome summary. Mirrors spec §5.3. */
function skippedReasonLabel(reason: string): string {
  switch (reason) {
    case 'duplicate_in_file':
      return 'duplicate in this file';
    case 'invalid_domain':
      return 'not a valid domain';
    case 'unsupported_scheme':
      return 'not a valid domain';
    case 'empty':
      return 'empty row';
    case 'scanned_recently':
      return 'scanned in the last 7 days';
    case 'over_batch_limit':
      return 'over the 50-per-import limit';
    // ADR-0240 Portal v2 QA (2026-10-10) — `founder@gmail.com` cells are
    // dropped on the server with this reason; the summary explains why.
    case 'free_email_domain':
      return 'personal email with no website';
    default:
      return reason;
  }
}

/** Group `{input, reason}` rows by reason + render a short summary line. */
export function summarizeSkipped(
  skipped: ReadonlyArray<{ input: string; reason: string }>,
): string | null {
  if (skipped.length === 0) return null;
  const by_reason = new Map<string, number>();
  for (const s of skipped) {
    by_reason.set(s.reason, (by_reason.get(s.reason) ?? 0) + 1);
  }
  const parts: string[] = [];
  for (const [reason, n] of by_reason) {
    parts.push(`${n} ${skippedReasonLabel(reason)}`);
  }
  return `Skipped ${skipped.length}: ${parts.join(' · ')}.`;
}

function humanizeSendResult(r: Exclude<SendRowReportResult, { kind: 'ok' }>): string {
  switch (r.kind) {
    case 'consent_required':
      return 'Check the consent box before sending.';
    case 'invalid_email':
      return 'That email address looks invalid.';
    case 'row_not_ready':
      return 'That scan has not finished yet. Try again in a minute.';
    case 'blocked':
      // Server deliberately omits the specific suppression bucket on
      // the wire; the copy is intentionally general.
      return 'Sill can’t send to that recipient. Use a different address.';
    case 'send_failed':
      return 'The send didn’t go through. Try again in a few minutes or contact Sill.';
    case 'invalid_subject':
      return 'That subject line is not allowed. Keep it short, drop any URLs, and avoid all caps.';
    case 'invalid_first_name':
      return 'That first name is not allowed. Letters, spaces, hyphens and apostrophes only.';
    case 'rate_limited':
      return r.message;
    case 'forbidden':
      return 'You do not have permission to do this.';
    case 'error':
      return r.message;
  }
}

/** Smart-import preview state (ADR-0240 Portal v2 §5.3; wired as
 *  part of the Agency gaps fix 2026-10-11). The parser produces
 *  typed prospect rows + a per-row skip breakdown; a separate call
 *  to `/v1/agency/reports/check-existing` marks rows already scanned
 *  in the last 7 days so the user can opt back in with a single
 *  checkbox before submitting. */
interface ParsedImport {
  readonly rows: readonly ParsedProspectRow[];
  readonly skipped: readonly SkippedProspectRow[];
  readonly file_label: string | null;
  readonly columns: readonly ImportColumnUse[];
}

/** Which source column fed each field, for the preview's "Columns"
 *  line. `header` is the file's own header text when one was found. */
function describeColumns(
  grid: readonly (readonly string[])[],
  p: ParseResult,
): readonly ImportColumnUse[] {
  const header_row = p.columns.header_row_detected ? grid[0] ?? null : null;
  const out: ImportColumnUse[] = [];
  const fields: readonly [ImportColumnUse['field'], number | null][] = [
    ['domain', p.columns.domain],
    ['company', p.columns.company],
    ['contact_name', p.columns.contact_name],
    ['email', p.columns.email],
  ];
  for (const [field, idx] of fields) {
    if (idx === null || idx < 0) continue;
    const h = header_row?.[idx]?.trim() ?? '';
    out.push({ field, header: h.length > 0 ? h : null });
  }
  return out;
}

/** Turn pasted text into a grid.
 *  - cells copied from a spreadsheet arrive tab-separated → columns;
 *  - a pasted CSV with a recognised header row → columns;
 *  - otherwise a plain list: one domain or email per line, commas and
 *    semicolons also separate (the pre-smart-import behaviour). */
function gridFromPaste(text: string): string[][] {
  const clean = text.startsWith('\uFEFF') ? text.slice(1) : text;
  if (clean.includes('\t')) return parseDelimited(clean, '\t');
  const sniffed = parseDelimited(clean);
  if (detectColumns(sniffed).header_row_detected) return sniffed;
  return clean
    .split(/[\r\n,;]+/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map((v) => [v]);
}

function toParsedImport(
  grid: readonly (readonly string[])[],
  file_label: string | null,
): ParsedImport {
  const p = parseProspectGrid(grid);
  return {
    rows: p.rows,
    skipped: p.skipped,
    file_label,
    columns: describeColumns(grid, p),
  };
}

function xlsxErrorCopy(reason: string): string {
  switch (reason) {
    case 'file_too_large':
      return 'That file is larger than 2 MB. Export fewer rows, or save as CSV and upload that.';
    case 'legacy_xls':
      return 'This looks like an older Excel file (.xls). Save it as .xlsx or CSV and try again.';
    case 'encrypted':
      return 'That Excel file is password-protected. Remove the password and save it again.';
    case 'decompress_unavailable':
      return 'Your browser cannot read Excel files here. Save as CSV and upload that instead.';
    case 'entry_too_large':
    case 'malformed':
    case 'not_zip':
      return 'We could not read that Excel file. Save as CSV and upload that instead.';
    case 'empty_workbook':
      return 'That workbook is empty.';
    default:
      return 'We could not read that file.';
  }
}

/** Read `?run=<run_id>` from the current URL — the ADR §Open-questions
 *  pattern mirrors the Mandates `?record=` deep-link. Returns null
 *  outside a browser env or when the param is missing / wrong shape. */
function readRunIdParam(): string | null {
  try {
    const g = globalThis as unknown as { location?: { search?: string } };
    const search = g.location?.search ?? '';
    const m = /[?&]run=(abr_[0-9A-HJKMNP-TV-Z]{26})(?:&|$)/.exec(search);
    return m !== null ? (m[1] as string) : null;
  } catch {
    return null;
  }
}

/** Persist (or clear) the selected `run_id` on the URL via
 *  replaceState — same pattern the SPA uses for `?view=`. */
function writeRunIdParam(run_id: string | null): void {
  try {
    const url = new URL(window.location.href);
    if (run_id === null) url.searchParams.delete('run');
    else url.searchParams.set('run', run_id);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* non-browser env; no-op */
  }
}

export function AgencyReports(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const { features, acting } = useAgency();
  const [input, setInput] = useState('');
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const [run, setRun] = useState<BulkRunWire | null>(null);
  const [sendDialog, setSendDialog] = useState<SendDialogState | null>(null);
  const [overview, setOverview] = useState<AgencyReportsOverviewWire | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** Agency Portal v4 UX (2026-10-11) — anchor for "View results" to
   *  scroll the ReportsTable into view after the import modal closes
   *  on a submit with skipped rows. */
  const reportsTableRef = useRef<HTMLDivElement | null>(null);
  /** v3 (2026-10-10) — Import is a Modal reached from the page header's
   *  primary action. The old "paste at the top of the page" plus a
   *  second "Recent runs" plus a "Selected run" table was three
   *  overlapping surfaces for one job. The modal keeps the paste +
   *  upload flow intact; results appear in the single Reports table
   *  below. */
  const [importOpen, setImportOpen] = useState(false);
  /** Smart-import state (Agency gaps fix 2026-10-11; ADR-0240 Portal
   *  v2 §5). The parser produces typed prospect rows + a per-row
   *  skip breakdown; `existing` holds the server's "scanned in the
   *  last 7 days" answer; `rescanRecent` is the opt-in checkbox. */
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [existing, setExisting] = useState<readonly CheckExistingResultRow[]>([]);
  const [rescanRecent, setRescanRecent] = useState(false);
  const [parseNotice, setParseNotice] = useState<string | null>(null);


  const selected_run_id =
    submit.kind === 'ok' ? submit.run_id : run?.run_id ?? null;

  // ─── Mount: load overview + restore ?run= ─────────────────────
  useEffect(() => {
    if (http === null) return;
    if (features === null) return;
    if (features.agency !== true) return;
    if (acting !== null) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      const r = await getReportsOverview(http);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setOverview(r.overview);
        setOverviewError(null);
      } else if (r.kind === 'error' || r.kind === 'forbidden') {
        setOverviewError(r.kind === 'forbidden' ? 'Agency access is not available.' : r.message);
      }
    };
    void run();
    return (): void => {
      cancelled = true;
    };
  }, [http, features, acting]);

  // Restore the deep-link once overview + http are both ready.
  useEffect(() => {
    if (http === null || overview === null) return;
    if (submit.kind !== 'idle') return;
    const restore = readRunIdParam();
    if (restore === null) return;
    // Only auto-restore when the run is in the recent-20 history, else
    // leave the URL param in place and let the agency pick a run from
    // the list.
    const match = overview.runs.find((r) => r.run_id === restore);
    if (match === undefined) return;
    setSubmit({ kind: 'ok', run_id: restore });
  }, [http, overview, submit.kind]);

  // ─── Polling loop ─────────────────────────────────────────────
  useEffect(() => {
    if (http === null) return;
    if (submit.kind !== 'ok') return;
    const run_id = submit.run_id;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      const r = await pollBulkRun(http, run_id);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setRun(r.run);
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      if (
        run !== null
        && run.rows.every((row) => row.status !== 'queued' && row.status !== 'running')
      ) {
        window.clearInterval(timer);
        return;
      }
      void tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // The `run` dependency is intentionally omitted; we re-run only on
    // a new submit (the interval reads the latest `run` via closure).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [http, submit.kind === 'ok' ? submit.run_id : null]);

  const resetImportStage = useCallback((): void => {
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
    setParsed(null);
    setExisting([]);
    setRescanRecent(false);
    setParseNotice(null);
    setInput('');
  }, []);

  const runPreview = useCallback(
    async (p: ParsedImport): Promise<void> => {
      setParsed(p);
      setRescanRecent(false);
      setExisting([]);
      setParseNotice(null);
      if (http === null || p.rows.length === 0) return;
      const r = await checkExistingDomains(
        http,
        p.rows.map((row) => row.domain),
      );
      if (r.kind === 'ok') {
        setExisting(r.existing);
      }
    },
    [http],
  );

  const onPastePreview = useCallback(async (): Promise<void> => {
    if (input.trim().length === 0) return;
    await runPreview(toParsedImport(gridFromPaste(input), null));
  }, [input, runPreview]);

  const onSubmit = useCallback(async (): Promise<void> => {
    if (http === null) return;
    if (parsed === null || parsed.rows.length === 0) return;
    setSubmit({ kind: 'submitting' });
    // ADR-0240 Portal v2 §5.5 — the request body carries full
    // prospect rows with optional contact fields so the server can
    // persist them on `agency_bulk_scan_row` and the per-row Send
    // dialog can prefill from them. The server accepts the legacy
    // `{ domains: string[] }` as a one-release fallback.
    const rows_body = parsed.rows.map((row) => ({
      domain: row.domain,
      ...(row.email !== null ? { email: row.email } : {}),
      ...(row.company !== null ? { company: row.company } : {}),
      ...(row.contact_name !== null ? { contact_name: row.contact_name } : {}),
    }));
    const r = await submitBulkImport(http, rows_body, {
      rescan_recent: rescanRecent,
    });
    if (r.kind === 'ok') {
      setSubmit({ kind: 'ok', run_id: r.run_id, skipped: r.skipped });
      writeRunIdParam(r.run_id);
      // Seed the run state immediately so the UI renders without the
      // first poll's round-trip.
      setRun({
        run_id: r.run_id,
        agency_account_id: '',
        created_at: new Date().toISOString(),
        domains_submitted: rows_body.length,
        domains_accepted: r.rows.filter((row) => row.status === 'queued').length,
        status: 'running',
        rows: r.rows,
        remaining_budget: r.remaining_budget,
      });
      // Refresh the overview so the budget line reflects the burnt
      // slots. v3 removes the separate "Recent runs" card so no run
      // list refresh is needed; the Reports table polls its own rows.
      const refreshed = await getReportsOverview(http);
      if (refreshed.kind === 'ok') setOverview(refreshed.overview);
      resetImportStage();
      // Close the import modal once the submit is accepted; the
      // results flow into the table below. If skipped rows exist we
      // keep the modal open so the summary is readable.
      if (r.skipped === undefined || r.skipped.length === 0) {
        setImportOpen(false);
      }
      return;
    }
    setSubmit({ kind: 'error', message: humanizeSubmit(r) });
  }, [http, parsed, rescanRecent, resetImportStage]);

  const onFileSelect = useCallback(
    async (file: File | null): Promise<void> => {
      if (file === null) return;
      setParseNotice(null);
      const name_lc = file.name.toLowerCase();
      // Legacy OLE Excel — hint the user to save as .xlsx or CSV.
      if (name_lc.endsWith('.xls') && !name_lc.endsWith('.xlsx')) {
        setParseNotice(
          'This looks like an older Excel file (.xls). Save it as .xlsx or CSV and try again.',
        );
        return;
      }
      if (name_lc.endsWith('.xlsx')) {
        const r = await readXlsx(file);
        if (!r.ok) {
          setParseNotice(xlsxErrorCopy(r.reason));
          return;
        }
        await runPreview(toParsedImport(r.grid, file.name));
        return;
      }
      // CSV / TSV / TXT — read as text + delimiter sniff.
      const text = await file.text();
      const delim: ',' | ';' | '\t' | undefined = name_lc.endsWith('.tsv')
        ? '\t'
        : undefined;
      await runPreview(toParsedImport(parseDelimited(text, delim), file.name));
    },
    [runPreview],
  );

  // v3 (2026-10-10) — per-run CSV export + run selection moved into
  // the ReportsTable's filter-driven CSV export. The old
  // "Recent runs" / "Selected run" cards were deleted along with their
  // handlers; `selected_run_id` is kept for URL state because the
  // Modal flow still writes `?run=` and the deep-link restore reads it.

  const openSendDialog = (
    row: BulkRowWire,
    prefill?: {
      email?: string | null;
      contact_name?: string | null;
    },
  ): void => {
    const default_subject = defaultAgencySendSubject(row.domain);
    const first_name = prefill?.contact_name !== undefined && prefill.contact_name !== null
      ? prefill.contact_name.split(/\s+/)[0] ?? ''
      : '';
    setSendDialog({
      row,
      email: prefill?.email ?? '',
      consent: false,
      submitting: false,
      error: null,
      sent_url: null,
      subject: default_subject,
      first_name,
    });
  };

  const onConfirmSend = useCallback(async (): Promise<void> => {
    if (http === null || sendDialog === null) return;
    setSendDialog({ ...sendDialog, submitting: true, error: null });
    const r = await sendRowReport(http, sendDialog.row.row_id, {
      email: sendDialog.email,
      consent_attested: sendDialog.consent,
      ...(sendDialog.subject.length > 0 ? { subject: sendDialog.subject } : {}),
      ...(sendDialog.first_name.length > 0 ? { first_name: sendDialog.first_name } : {}),
    });
    if (r.kind === 'ok') {
      setSendDialog({ ...sendDialog, submitting: false, sent_url: r.report_url });
      return;
    }
    setSendDialog({ ...sendDialog, submitting: false, error: humanizeSendResult(r) });
  }, [http, sendDialog]);

  // v4 UX (2026-10-11) — after a submit with skipped rows the modal
  // used to leave a disabled "Scan 0 domains" button (`domains.length`
  // was reset to 0 by `setInput('')`), with only "Close" as the way
  // out. Replace that with a clear primary "View results" that closes
  // the modal, filters the table to the just-created run, and scrolls
  // the table into view — the skip summary stays rendered above it.
  //
  // Hook order (React) — this `useCallback` MUST live above the
  // guard-clause early returns below; the first render returns the
  // Loading shell (features === null), later renders return the full
  // layout, and declaring hooks on only one of those paths crashes
  // with "Rendered more hooks than during the previous render".
  const onViewResults = useCallback((): void => {
    setImportOpen(false);
    // Scroll the ReportsTable into view on the next tick so the modal
    // has finished its close transition before the browser scrolls.
    // `scrollIntoView` is a browser-only API — JSDOM (vitest) doesn't
    // implement it, so the typeof-guard keeps the component testable
    // without a jsdom-polyfill side effect.
    window.requestAnimationFrame(() => {
      const el = reportsTableRef.current;
      if (el !== null && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, []);

  // ─── Guardrails ───────────────────────────────────────────────
  if (features === null) {
    return (
      <ReportsShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        </Card>
      </ReportsShell>
    );
  }
  if (acting !== null) {
    return (
      <ReportsShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Return to your home account to run reports.
          </div>
        </Card>
      </ReportsShell>
    );
  }
  if (features.agency !== true) {
    return (
      <ReportsShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>
            Reports are an agency add-on.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Contact Sill to enable the agency surface on this account.
          </div>
        </Card>
      </ReportsShell>
    );
  }

  // Prefer the live run's remaining_budget (fresher on submit) but
  // fall back to the overview load for the on-mount case.
  const remaining =
    run?.remaining_budget ?? overview?.remaining_budget ?? null;
  const cap = overview?.cap ?? null;
  const budgetLine =
    remaining !== null && cap !== null
      ? `${remaining} of ${cap} scans left today`
      : remaining !== null
        ? `${remaining} scans left today`
        : null;
  const agencyName = overview?.display_name ?? DEFAULT_AGENCY_NAME;
  // ADR-0240 Portal v2 Slice 6 — Reports table → send bridge. The
  // table surfaces ReportTableRow; the Send dialog operates on
  // BulkRowWire. Shim the row shape so the dialog can be the single
  // source of truth for subject + first_name + consent.
  const openSendFromTable = (row: ReportTableRow): void => {
    const bulk_row: BulkRowWire = {
      row_id: row.row_id,
      domain: row.domain,
      status: row.status,
      report_slug: row.report_slug,
      report_url: row.report_url,
      signals_present: row.signals_present,
      signals_total: row.signals_total,
      outcome: row.outcome,
      platform: row.platform,
      top_findings: row.top_findings,
    };
    openSendDialog(bulk_row, {
      email: row.contact_email,
      contact_name: row.contact_name,
    });
  };

  // v3 (2026-10-10) — the Reports section has ONE primary action
  // ("Scan domains") and ONE results surface (the cross-run table).
  // The old split (Reports table at top, paste card middle, Recent
  // runs card, Selected run card) created duplicate rows and a
  // "wacky" shape; the modal collapses the import step into a focused
  // flow and the table is the single truth below.
  //
  // v4 UX (2026-10-11) — `focusRunIdOnTable` + `onViewResults` are
  // read here; the actual `useCallback` for `onViewResults` lives
  // above the guard clauses so hook order never changes across
  // first-paint vs ready renders. See the matching comment there.
  const focusRunIdOnTable =
    submit.kind === 'ok' ? submit.run_id : null;
  return (
    <ReportsShell>
      {/* Header: budget line + primary action, right-aligned. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 14,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        {budgetLine !== null ? (
          <div
            data-testid="agency-reports-budget"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.3,
            }}
          >
            {budgetLine}
          </div>
        ) : null}
        <Button
          variant="primary"
          size="md"
          data-testid="agency-reports-open-import"
          onClick={() => {
            setSubmit({ kind: 'idle' });
            resetImportStage();
            setImportOpen(true);
          }}
          icon={Upload}
        >
          Scan domains
        </Button>
      </div>

      {/* Primary results surface. */}
      {http !== null ? (
        <div ref={reportsTableRef}>
          <Card className="p-5 sm:p-6">
            <ReportsTable
              http={http}
              onEmail={openSendFromTable}
              focusRunId={focusRunIdOnTable}
            />
          </Card>
        </div>
      ) : null}

      {/* Import modal — two-stage flow: paste/upload → preview → submit. */}
      <Modal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          resetImportStage();
        }}
        size="md"
        title="Scan domains"
        closeOnBackdrop={false}
      >
        <div style={{ padding: 20 }}>
          {/* Stage 2: preview — rendered as soon as `parsed` is set,
              UNLESS the submit has succeeded with a non-empty skip
              list (handled by the skipped summary below). */}
          {parsed !== null
          && !(submit.kind === 'ok' && submit.skipped !== undefined && submit.skipped.length > 0) ? (
            <>
              <ImportPreview
                rows={parsed.rows}
                skipped={parsed.skipped}
                existing={existing}
                rescan_recent={rescanRecent}
                remaining_budget={remaining}
                submitting={submit.kind === 'submitting'}
                file_label={parsed.file_label}
                columns={parsed.columns}
                onToggleRescan={(next) => setRescanRecent(next)}
                onSubmit={() => void onSubmit()}
                onCancel={() => {
                  // Back to the paste/upload step, keeping any pasted text.
                  setParsed(null);
                  setExisting([]);
                  setRescanRecent(false);
                  setSubmit({ kind: 'idle' });
                  if (fileInputRef.current !== null) fileInputRef.current.value = '';
                }}
              />
              {submit.kind === 'error' ? (
                <div style={{ marginTop: 14 }}>
                  <div
                    role="alert"
                    data-testid="agency-reports-submit-error"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                      background: C.coralDim,
                      border: `1px solid ${C.border}`,
                      borderLeft: `3px solid ${C.coralFg}`,
                      borderRadius: 3,
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      lineHeight: 1.5,
                    }}
                  >
                    <AlertCircle
                      size={14}
                      color={C.coralFg}
                      style={{ flexShrink: 0, marginTop: 2 }}
                      aria-hidden="true"
                    />
                    <span>{submit.message}</span>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {/* Stage 1: paste + upload. Hidden while the preview stage
              OR a post-submit skip summary is on screen. */}
          {parsed === null && submit.kind !== 'ok' ? (
            <>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.muted,
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}
              >
                Paste domains or emails (one per line), or upload a .csv, .tsv, .txt or .xlsx file.
                Up to 50 per batch. Each domain uses one of your daily scans.
              </div>
              <textarea
                id="agency-reports-input"
                data-testid="agency-reports-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="example.com&#10;jo@another.com"
                rows={8}
                style={{
                  width: '100%',
                  fontFamily: monoFont,
                  fontSize: 13,
                  color: C.text,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  padding: 10,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv,.tsv,.xlsx,text/plain,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => void onFileSelect(e.target.files?.[0] ?? null)}
                  style={{ display: 'none' }}
                  data-testid="agency-reports-file"
                />
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => fileInputRef.current?.click()}
                  icon={Upload}
                >
                  Upload .csv / .tsv / .txt / .xlsx
                </Button>
              </div>
              {parseNotice !== null ? (
                <div
                  role="alert"
                  data-testid="agency-reports-parse-notice"
                  style={{
                    marginTop: 14,
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    background: C.coralDim,
                    border: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${C.coralFg}`,
                    borderRadius: 3,
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    lineHeight: 1.5,
                  }}
                >
                  <AlertCircle size={14} color={C.coralFg} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                  <span>{parseNotice}</span>
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 10,
                  marginTop: 20,
                }}
              >
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setImportOpen(false);
                    resetImportStage();
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  data-testid="agency-reports-preview"
                  onClick={() => void onPastePreview()}
                  disabled={input.trim().length === 0}
                >
                  Next
                </Button>
              </div>
            </>
          ) : null}

          {submit.kind === 'ok' && submit.skipped !== undefined && submit.skipped.length > 0 ? (
            <>
              <div
                role="status"
                data-testid="agency-reports-submit-skipped"
                style={{
                  marginTop: 2,
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.text,
                  background: C.amberDim,
                  border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${C.amberFg}`,
                  borderRadius: 3,
                  padding: '10px 12px',
                  lineHeight: 1.5,
                }}
              >
                {summarizeSkipped(submit.skipped)}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 10,
                  marginTop: 20,
                }}
              >
                <Button
                  variant="primary"
                  size="md"
                  data-testid="agency-reports-view-results"
                  onClick={onViewResults}
                >
                  View results
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </Modal>

      {/* Send dialog */}
      <Modal
        open={sendDialog !== null}
        onClose={() => setSendDialog(null)}
        size="sm"
        title={sendDialog !== null ? `Email the report for ${sendDialog.row.domain}` : null}
        closeOnBackdrop={false}
      >
        {sendDialog !== null ? (
          <SendDialogBody
            state={sendDialog}
            agency_name={agencyName}
            onChange={(next) => setSendDialog({ ...sendDialog, ...next })}
            onConfirm={() => void onConfirmSend()}
            onClose={() => setSendDialog(null)}
          />
        ) : null}
      </Modal>

      {overviewError !== null && overview === null ? (
        <div style={{ marginTop: 14 }}>
          <div
            role="status"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              padding: 12,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              background: C.surface,
            }}
          >
            We could not load your scan budget just now. Refresh to try again.
          </div>
        </div>
      ) : null}
    </ReportsShell>
  );
}

// v3 (2026-10-10) — RecentRunRow / ReportRow and per-run copy
// handlers were deleted along with the "Recent runs" and "Selected
// run" cards. The cross-run ReportsTable is the single surface now.

interface SendDialogBodyProps {
  readonly state: SendDialogState;
  readonly agency_name: string;
  readonly onChange: (patch: Partial<SendDialogState>) => void;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

function SendDialogBody({
  state,
  agency_name,
  onChange,
  onConfirm,
  onClose,
}: SendDialogBodyProps): JSX.Element {
  const C = useC();
  const canSend =
    state.email.length > 0
    && state.consent
    && state.subject.trim().length > 0
    && state.subject.length <= 120
    && !state.submitting
    && state.sent_url === null;

  // ADR-0240 Portal v2 Slice 1 (2026-10-10) — the dialog ALWAYS
  // ends in a visible terminal state. The success panel is lime-
  // tinted and names the recipient so the agency knows the send
  // reached a concrete address, not that a request was accepted.
  if (state.sent_url !== null) {
    return (
      <div style={{ padding: 20 }}>
        <div
          data-testid="agency-reports-send-success"
          role="status"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            padding: '10px 12px',
            background: C.limeDim,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.limeFg}`,
            borderRadius: 3,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Check size={14} color={C.limeFg} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ lineHeight: 1.5 }}>
            <div data-testid="agency-reports-send-success-headline">
              Sent to <strong>{state.email}</strong>.
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
              It arrives from <code style={{ fontFamily: monoFont }}>hello@sill.so</code> within a minute.
            </div>
          </div>
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.muted,
            padding: '8px 10px',
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            wordBreak: 'break-all',
            marginBottom: 16,
          }}
        >
          {state.sent_url}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            height: 32,
            padding: '0 14px',
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <label
        htmlFor="agency-reports-send-email"
        style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, textTransform: 'uppercase' }}
      >
        Recipient email
      </label>
      <input
        id="agency-reports-send-email"
        data-testid="agency-reports-send-email"
        type="email"
        value={state.email}
        onChange={(e) => onChange({ email: e.target.value })}
        style={{
          width: '100%',
          marginTop: 6,
          fontFamily: monoFont,
          fontSize: 13,
          color: C.text,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          padding: '7px 10px',
        }}
      />
      {/* ADR-0240 Portal v2 Slice 7 — editable subject + optional
          greeting first name. Both pre-filled from the import row's
          contact fields; agency can override before send. */}
      <label
        htmlFor="agency-reports-send-first-name"
        style={{
          display: 'block',
          marginTop: 14,
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        First name (optional)
      </label>
      <input
        id="agency-reports-send-first-name"
        data-testid="agency-reports-send-first-name"
        type="text"
        value={state.first_name}
        onChange={(e) => onChange({ first_name: e.target.value })}
        placeholder="Jane"
        style={{
          width: '100%',
          marginTop: 6,
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          padding: '7px 10px',
        }}
      />
      <div
        style={{
          marginTop: 4,
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
        }}
      >
        {state.first_name.length > 0
          ? `Email will open with "Hi ${state.first_name},"`
          : 'Letters, spaces, hyphens and apostrophes. 1-40 characters.'}
      </div>
      <label
        htmlFor="agency-reports-send-subject"
        style={{
          display: 'block',
          marginTop: 14,
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        Subject
      </label>
      <input
        id="agency-reports-send-subject"
        data-testid="agency-reports-send-subject"
        type="text"
        value={state.subject}
        onChange={(e) => onChange({ subject: e.target.value })}
        maxLength={120}
        style={{
          width: '100%',
          marginTop: 6,
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          padding: '7px 10px',
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
        }}
      >
        <button
          type="button"
          onClick={() =>
            onChange({
              subject: defaultAgencySendSubject(state.row.domain),
            })
          }
          style={{
            background: 'transparent',
            border: 'none',
            color: C.muted,
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            textDecoration: 'underline',
          }}
        >
          Reset to default
        </button>
        <span>{state.subject.length} / 120</span>
      </div>
      <label
        htmlFor="agency-reports-send-consent"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          marginTop: 14,
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.5,
          cursor: 'pointer',
        }}
      >
        <input
          id="agency-reports-send-consent"
          data-testid="agency-reports-send-consent"
          type="checkbox"
          checked={state.consent}
          onChange={(e) => onChange({ consent: e.target.checked })}
          style={{ marginTop: 3 }}
        />
        <span data-testid="agency-reports-send-consent-copy">
          I confirm this recipient agreed to receive readiness reports from {agency_name}.
        </span>
      </label>
      {/* ADR-0240 Portal v2 Slice 1 — the error renders in a visibly
          distinct coral panel (not the same grey style as the
          subject preview above). Positioned DIRECTLY above the
          action buttons so a user who looked at the Send button
          doesn't miss it. `role=alert` moves the SR focus. */}
      {state.error !== null ? (
        <div
          role="alert"
          data-testid="agency-reports-send-error"
          style={{
            marginTop: 12,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            background: C.coralDim,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.coralFg}`,
            borderRadius: 3,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            lineHeight: 1.5,
          }}
        >
          <AlertCircle
            size={14}
            color={C.coralFg}
            style={{ flexShrink: 0, marginTop: 2 }}
            aria-hidden="true"
          />
          <span>{state.error}</span>
        </div>
      ) : null}
      {state.submitting ? (
        <div
          data-testid="agency-reports-send-progress"
          style={{
            marginTop: 10,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 0.5,
          }}
        >
          Sending… this can take up to 20 seconds.
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            height: 32,
            padding: '0 14px',
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            background: C.surface,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canSend}
          data-testid="agency-reports-send-confirm"
          style={{
            height: 32,
            padding: '0 14px',
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            background: canSend ? C.lime : C.surface2,
            color: canSend ? C.ink : C.muted,
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: canSend ? 600 : 400,
            opacity: canSend ? 1 : 0.6,
          }}
          aria-busy={state.submitting ? true : undefined}
        >
          {state.submitting ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function ReportsShell({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  const embedded = useIsEmbedded();
  if (embedded) {
    // AgencyPortal already renders the kicker, H1 and container.
    return <>{children}</>;
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={2}>Agency</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Reports.
        </h1>
      </div>
      {children}
    </div>
  );
}
